// src/utils/compSortingSections.ts

import { type SortSectionConfig } from "@/components/sorting/ReusableSortPopup"
import { sortOptions } from "@/types/compTypes"
import { useStore } from "@nanostores/react"
import { currentCriterionStore } from "@/stores/compSortingDataStore"

const useCompSortingSections = (): SortSectionConfig[] => {
  const selectedSortingCriterion = useStore(currentCriterionStore)

  /**
   * Utility function to get ascending and descending labels based on criterion
   * @param criterion
   */
  const getOrderingLabels = (criterion: string | null) => {
    if (!criterion) {
      return null
    }
    switch (criterion) {
      case "distance":
        return { ascending: "Near to Far", descending: "Far to Near" }
      case "pricePerSqft":
        return { descending: "Low to High", ascending: "High to Low" }
      case "soldPrice":
        return { descending: "Low to High", ascending: "High to Low" }
      case "purchase_price":
        return { ascending: "Low to High", descending: "High to Low" }
      case "beds":
      case "baths":
        return { ascending: "Low to High", descending: "High to Low" }
      case "squareFeet":
        return { descending: "Low to High", ascending: "High to Low" }
      case "yearBuilt":
        return { descending: "Oldest to Newest", ascending: "Newest to Oldest" }
      case "soldDate":
        return { descending: "Oldest to Recent", ascending: "Recent to Oldest" }
      case "suggested":
      case "updatedAt":
        return null // No ordering label for updatedAt or suggested
      default:
        return { ascending: "Ascending", descending: "Descending" }
    }
  }

  const orderingLabels = getOrderingLabels(selectedSortingCriterion)

  const sortSection: SortSectionConfig = {
    header: "SORT",
    options: Object.entries(sortOptions)
      .filter(([_, sortOption]) => sortOption.uiDisplay !== false) // Only include options with uiDisplay true
      .map(([label, sortOption]) => ({
        label,
        value: sortOption.criterion,
        arrowIcon: null,
      })),
  }

  const orderingSection: SortSectionConfig | null = orderingLabels
    ? {
        header: "ORDERING",
        options: [
          { label: orderingLabels.ascending, value: "default" },
          { label: orderingLabels.descending, value: "reverse" },
        ].filter((option) => option.label !== null),
      }
    : null

  // Filter out any null or empty sections
  return [sortSection, orderingSection].filter(
    (section): section is SortSectionConfig =>
      section !== null && section.options.length > 0
  )
}

export default useCompSortingSections
